import { CKButton } from '@clearkit/react';

import { EMPTY_STATE_SEARCH } from '~/components/SearchBuilder/utils/constants';
import { isValidSearch } from '~/components/SearchBuilder/utils/validateSearch';
import { audienceSubtypes } from '~/lib/audiences';
import { segmentTypes } from '~/lib/segments';

import SegmentBuilder from './SegmentBuilder';
import SegmentPicker from './SegmentPicker';

export default function AudiencesSegmentForm({
  companyCollections,
  companySearchAttributes,
  disabled,
  onChange,
  onValidate,
  personCollections,
  personSearchAttributes,
  values: { externalName, segment, subtype },
  title,
  showPreview,
  actions,
}) {
  // Prioritize user input and fall back to segment name if possible
  const buildExternalName = (newSegment) =>
    externalName === segment?.name ? newSegment.name : externalName;

  useEffect(() => {
    onValidate({
      externalName: !!externalName,
      segmentCollection: segment && segment.collectionId,
      segmentName: segment && segment.name,
      segmentSearch: segment && isValidSearch(segment.search),
    });
  }, [segment?.search, segment?.name, segment?.collectionId, externalName]);

  const rel =
    segment?.type === segmentTypes.company
      ? 'company-builder'
      : 'person-builder';

  return (
    <div rel={rel}>
      {(() => {
        if (segment) {
          const [collections, searchAttributes] =
            segment.type === segmentTypes.company
              ? [companyCollections, companySearchAttributes]
              : [personCollections, personSearchAttributes];

          return (
            <SegmentBuilder
              actions={actions}
              showPreview={showPreview}
              title={title}
              segment={segment}
              subtype={subtype}
              disabled={disabled}
              collections={collections}
              searchAttributes={searchAttributes}
              collectionId={segment.collectionId}
              name={segment.name}
              onChange={(newSegment) => {
                const newExternalName = buildExternalName(newSegment);

                onChange({
                  externalName: newExternalName,
                  segment: { ...segment, ...newSegment },
                });
              }}
              onClone={() => {
                const clonedSegment = {
                  ...segment,
                  id: undefined,
                  name: `Clone of ${segment.name}`,
                };

                onChange({
                  segment: clonedSegment,
                });
              }}
              search={segment.search || EMPTY_STATE_SEARCH}
              onCancel={() => {
                onChange({
                  externalName: undefined,
                  segment: undefined,
                });
              }}
              footerActions={
                <CKButton
                  variant="simple"
                  onClick={() => {
                    onChange({
                      externalName: undefined,
                      segment: undefined,
                    });
                  }}>
                  Choose a segment
                </CKButton>
              }
            />
          );
        }

        return (
          <SegmentPicker
            title={title}
            companyCollections={companyCollections}
            personCollections={personCollections}
            onChange={(newSegment) => {
              const newExternalName = buildExternalName(newSegment);

              onChange({
                externalName: newExternalName,
                segment: newSegment,
              });
            }}
            type={subtype === audienceSubtypes.contacts ? 'All' : 'Company'}
            footerActions={
              <>
                <CKButton
                  onClick={() => {
                    onChange({
                      segment: { type: segmentTypes.company },
                    });
                  }}
                  variant="simple"
                  variantColor="blue">
                  Create a company segment
                </CKButton>

                {subtype === audienceSubtypes.contacts && (
                  <CKButton
                    onClick={() => {
                      onChange({
                        segment: { type: segmentTypes.person },
                      });
                    }}
                    variant="simple"
                    variantColor="blue">
                    Create a person segment
                  </CKButton>
                )}
              </>
            }
          />
        );
      })()}
    </div>
  );
}

AudiencesSegmentForm.propTypes = {
  companyCollections: PropTypes.array.isRequired,
  companySearchAttributes: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  personCollections: PropTypes.array,
  personSearchAttributes: PropTypes.array,
  values: PropTypes.object.isRequired,
  title: PropTypes.node,
};
